import { useAccountStore } from '@/store/accountStore.ts';
import { trackEvent } from '@/telemetry/trackEvent.ts';
import { interceptEmbeddedSignIn } from './embeddedAuthGate.ts';

export interface GenerateGateOptions {
  /** No signed-in account. */
  needsAuth: boolean;
  /** Balance is real (not beta) and cannot cover this generation's estimate. */
  insufficient: boolean;
  /**
   * Gets the host out of the way before a depletion / purchase dialog is raised — those render
   * at the app level and would otherwise open UNDER the surface that raised them. Every caller
   * passes the same close it gives its own Cancel.
   */
  onCloseHost: () => void;
}

/**
 * The pre-flight every "spend credits now" button runs before it generates: forced native
 * sign-in, then the insufficient-balance fork (first depletion → the welcome dialog, otherwise
 * the token-purchase dialog).
 *
 * Returns TRUE when the gate took over and the caller must NOT generate.
 *
 * Extracted 2026-08-21 — this sequence was copied into four surfaces and a divergence in it
 * costs the user real money. `insufficient` is a parameter rather than something read here so a
 * caller can narrow it (StyleGradeTab's tutorial free-render bypass) without forking the gate.
 * AiDialogShell deliberately still inlines its own copy: TTS depends on that shell.
 */
export async function runGenerateGate({ needsAuth, insufficient, onCloseHost }: GenerateGateOptions): Promise<boolean> {
  if (interceptEmbeddedSignIn(needsAuth)) return true;
  if (!insufficient) return false;

  const store = useAccountStore.getState();
  if (!store.welcomeDepletionDismissedThisSession) {
    const hasPurchased = await store.checkHasEverPurchased();
    if (!hasPurchased) {
      onCloseHost();
      store.setWelcomeDepletionOpen(true);
      return true;
    }
  }
  onCloseHost();
  trackEvent('depletion_shown', { context: 'ai_panel' });
  // Re-read: checkHasEverPurchased above awaits, and the action identity is not guaranteed
  // stable across that gap.
  useAccountStore.getState().setTokenPurchaseDialogOpen(true);
  return true;
}
